import { Controller } from '@hotwired/stimulus'

const HIDDEN_CLASS = "d-none"

export default class extends Controller {
  static targets = [
    "template", "collection", "item", "addButton",
    // Dummy targets to shut up warnings
    "id", "destroy"
  ]

  static values = {
    disableAddButtonWith: { type: String, default: "disable" },
    itemsLimit: { type: Number, default: -1 }
  }

  static classes = ["disableAddButton"]

  get autofocus() {
    const autofocus = this.data.get("autofocus")

    if (autofocus) {
      return autofocus == "true"
    } else {
      return true
    }
  }

  get hasItemLimit() { return this.itemsLimitValue >= 0 }

  connect() {
    this._updateAddButton()
  }

  add({ params: { templateName } }) {
    this.addItemForTemplate(templateName)
  }

  addItemForTemplate(templateName) {
    if (!this._canAddItem()) { return }

    const fieldsHTML = this._prepareNewFieldsForTemplate(templateName)
    const itemTarget = this._insertFieldsHTML(fieldsHTML)

    if (this.autofocus) {
      this._applyAutofocus(itemTarget)
    }

    this._updateAddButton()
    this._triggerInsertEvent(itemTarget)
  }

  destroy(e) {
    const itemTarget = e.target.closest("*[data-nested-fields-target='item']")

    this._tryDestroyByHiding(itemTarget) || this._destroyByRemoving(itemTarget)
    this._updateAddButton()
    this._triggerDestroyEvent(itemTarget)
  }

  // ===================
  // = Fields Handling =
  // ===================

  // Creation
  _prepareNewFieldsForTemplate(templateName) {
    const templateTarget = this._getTemplateNamed(templateName)

    const now = new Date()
    const identifier = now.getTime()
    const templateHTML = templateTarget.innerHTML

    const html = templateHTML.replace(/IDENTIFIER/g, identifier)
    return html
  }

  _insertFieldsHTML(html) {
    this.collectionTarget.insertAdjacentHTML("beforeend", html)

    return this._getInsertedItem()
  }

  _getInsertedItem() {
    const { itemTargets } = this
    const itemTarget = itemTargets[itemTargets.length - 1]

    return itemTarget
  }

  _getTemplateName(button) {
    return button.dataset.nestedFieldsTemplate
  }

  _getTemplateNamed(templateName) {
    if (!templateName) {
      return this.templateTarget
    } else {
      return this.templateTargets.find((target) => target.dataset.nestedFieldsTemplate == templateName)
    }
  }

  _applyAutofocus(itemTarget) {
    if (!itemTarget) { return }

    const autofocusTarget = itemTarget.querySelector("[autofocus]")

    if (autofocusTarget) {
      autofocusTarget.focus()
    }
  }

  _canAddItem() {
    return !this.hasItemLimit || this._getActiveItems().length < this.itemsLimitValue
  }


  // Removal
  _tryDestroyByHiding(itemTarget) {
    if (this._isPersistedItem(itemTarget)) {
      this._destroyByHiding(itemTarget)

      return true
    } else {
      return false
    }
  }

  _destroyByHiding(itemTarget) {
    const destroyInput = this._getDestroyInput(itemTarget)

    destroyInput.value = "1"
    itemTarget.classList.add(HIDDEN_CLASS)
  }

  _destroyByRemoving(itemTarget) {
    itemTarget.remove()
  }

  _getRecordIdOfItem(itemTarget) {
    const idInputTarget = this._getIdInput(itemTarget)

    if (idInputTarget) {
      return idInputTarget.value
    } else {
      return null
    }
  }

  // helpers

  _getDestroyInput(itemTarget) {
    return itemTarget.querySelector("[data-nested-fields-target=destroy]")
  }

  _getIdInput(itemTarget) {
    return itemTarget.querySelector("[data-nested-fields-target=id]")
  }

  _isPersistedItem(itemTarget) {
    const recordId = this._getRecordIdOfItem(itemTarget)

    return recordId != null && recordId != ""
  }

  _isActiveItem(itemTarget) {
    if (this._isPersistedItem(itemTarget)) {
      const { value } = this._getDestroyInput(itemTarget)
      return value != "1" && value != "true"
    } else {
      return true
    }
  }

  _getActiveItems() {
    return this.itemTargets.filter((item) => this._isActiveItem(item))
  }

  // =======================
  // = Add Button Handling =
  // =======================

  _updateAddButton() {
    if (!this.hasItemLimit) { return }

    if (this._canAddItem()) {
      this._enableAddButton()
    } else {
      this._disableAddButton()
    }
  }

  _getAddButtonDisableStrategy() { return this.disableAddButtonWithValue }

  _enableAddButton() {
    const disableStrategy = this._getAddButtonDisableStrategy()

    if (disableStrategy == "disable") {
      this.addButtonTarget.disabled = false
    } else if (disableStrategy == "class") {
      this.addButtonTarget.classList.remove(...this.disableAddButtonClasses)
    }
  }

  _disableAddButton() {
    const disableStrategy = this._getAddButtonDisableStrategy()

    if (disableStrategy == "disable") {
      this.addButtonTarget.disabled = true
    } else if (disableStrategy == "class") {
      this.addButtonTarget.classList.add(...this.disableAddButtonClasses)
    }
  }

  // ==========
  // = Events =
  // ==========

  _triggerDestroyEvent(itemTarget) {
    this.dispatch("destroy", { target: itemTarget })
    this._triggerChangeEvent()
  }

  _triggerInsertEvent(itemTarget) {
    this.dispatch("inserted", { target: itemTarget })
    this._triggerChangeEvent()
  }

  _triggerChangeEvent() {
    this.dispatch("change")
  }
}
